import { useEffect, useState } from "react";
import { GET_MY_PERMISSIONS } from "../constants/apiUrls";
import { useDispatch } from "react-redux";
import { setUser } from "../redux/user";
import { setPermissions } from "../redux/permissions";
import { renewTokens, handleLogout, apiGetAuth } from "../calls/apiCalls";
import { localStorageKeys } from "../constants/page";
import { jwtDecode } from "jwt-decode";
import {
  customAccessLabel,
  setImpersonation,
} from "../redux/impersonation";
import { clearCompanyCatalogs, ensureCatalog } from "../redux/companyCatalogs";

import { AUTH_CHANNEL, getAccessToken } from "../utils/authStorage";

export default function useAppInitializer() {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const accessToken = localStorage.getItem(localStorageKeys.accessToken);

  const initializeApp = async () => {
    if (accessToken) {
      const _user = await getSetPermissions();
      dispatch(ensureCatalog("groups"));

      const decoded = jwtDecode(accessToken);
      const claim = (suffix) => {
        const shortName = suffix.replace(/^\//, "");
        const match = Object.entries(decoded).find(([key]) => {
          const name = key.toLowerCase();
          return name.endsWith(suffix) || name === shortName;
        });
        return match?.[1] ? String(match[1]) : "";
      };
      const selfId = claim("/primarysid");
      const actorId = claim("/sid");
      const actorName = claim("/actor");
      const preview = decoded.permission_preview === "true";
      const company =
        sessionStorage.getItem(localStorageKeys.impersonationCompany) || "";

      let userLabel = "";
      if (actorName && actorId && selfId && actorId.toLowerCase() !== selfId.toLowerCase()) {
        userLabel = `${_user?.firstName || ""} ${_user?.lastName || ""}`.trim();
      } else if (preview) {
        userLabel = customAccessLabel;
      }

      if (company || userLabel) {
        dispatch(setImpersonation({ company, user: userLabel }));
      }
    }

    setLoading(false);
  };

  const ACCESS_REFRESH_BUFFER_MS = 2 * 60 * 1000; // refresh 2 min before expiry
  const POLL_MS = 60 * 1000;

  function accessTokenExpiresAt(token) {
    try {
      const decoded = jwtDecode(token);
      return decoded?.exp ? decoded.exp * 1000 : 0;
    } catch {
      return 0;
    }
  }

  async function refreshIfNeeded({ force = false } = {}) {
    const accessToken = getAccessToken();
    const refreshToken = localStorage.getItem(localStorageKeys.refreshToken);

    if (!refreshToken) return;

    if (!accessToken) {
      await renewTokens();
      return;
    }

    const expiresAt = accessTokenExpiresAt(accessToken);
    const shouldRefresh =
      force || expiresAt - Date.now() < ACCESS_REFRESH_BUFFER_MS;

    if (shouldRefresh) {
      await renewTokens();
    }
  }

  const getSetPermissions = async () => {
    const response = await apiGetAuth(GET_MY_PERMISSIONS);
    if (!response || typeof response !== "object") return {};
    dispatch(setPermissions(response));
    const user = response?.userDto ? response?.userDto : {};
    dispatch(setUser(user));
    return user;
  };

  useEffect(() => {
    initializeApp();

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refreshIfNeeded();
      }
    };

    const onFocus = () => refreshIfNeeded();
    const onOnline = () => refreshIfNeeded({ force: true });

    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") {
        refreshIfNeeded();
      }
    }, POLL_MS);

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);

    let channel;
    try {
      channel = new BroadcastChannel(AUTH_CHANNEL);
      channel.onmessage = (event) => {
        if (event.data?.type === "logout") {
          handleLogout({ redirect: true });
        }
      };
    } catch {
      // ignore
    }

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      channel?.close();
    };
  }, []);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      dispatch(setUser({}));
      dispatch(clearCompanyCatalogs());
    }
  }, [dispatch]);

  return { loading };
}
